import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaArrowLeft,
  FaTasks,
  FaTrash,
} from "react-icons/fa";
import PageTemplate from "../components/PageTemplate";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import SchoolYearDropdown, { getInitialSchoolYearId, getSchoolYearLabel } from "../components/SchoolYearDropdown";
import { useCan } from "../authz/useCan";
import { teacherSchoolTasksApi } from "../features/schoolTasks/api";
import {
  filterSchoolActivityRows,
  getFamilyObligationRows,
  getSchoolActivityRows,
  type ActivityStatus,
} from "../features/schoolTasks/admin/schoolTaskAdminSelectors";
import { TasksSection } from "../features/schoolTasks/admin/TasksSection";
import { ActivitiesSection } from "../features/schoolTasks/admin/ActivitiesSection";
import { cleaningChildrenUndeterminedText, ObligationsFamilyActions, ObligationsSection } from "../features/schoolTasks/admin/ObligationsSection";
import { CleaningSection } from "../features/schoolTasks/admin/CleaningSection";
import { AutoAssignmentPreviewDialog } from "../features/schoolTasks/admin/AutoAssignmentPreviewDialog";
import { SettingsSection, useSchoolTaskSettings } from "../features/schoolTasks/admin/SettingsSection";
import { useSchoolTasksAdminData } from "../features/schoolTasks/admin/useSchoolTasksAdminData";
import { getActivityVisual } from "../features/schoolTasks/admin/schoolTaskAdminPresentation";
import {
  SchoolTaskEditor,
  type SchoolTaskFormValues,
} from "../features/schoolTasks/admin/SchoolTaskEditor";
import type {
  SchoolActivity,
  SchoolTask,
  SchoolTaskType,
} from "../features/schoolTasks/types";

const emptyTask: SchoolTaskFormValues = {
  name: "",
  description: "",
  type: "PARENT_TASK" as SchoolTaskType,
  schoolYearId: "",
  classroomIds: [] as string[],
  startsAt: "",
  capacity: 1,
  optional: false,
  activityId: "",
};
const emptyActivity = { name: "", description: "", startsAt: "", endsAt: "", schoolYearId: "" };
const localInput = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const iso = (value: string) => (value ? new Date(value).toISOString() : null);
const newActivityDefaults = (schoolYearId: string) => {
  const startsAt = new Date();
  startsAt.setHours(8, 30, 0, 0);
  const endsAt = new Date(startsAt);
  endsAt.setHours(15, 0, 0, 0);
  return {
    ...emptyActivity,
    schoolYearId,
    startsAt: localInput(startsAt.toISOString()),
    endsAt: localInput(endsAt.toISOString()),
  };
};
export default function SchoolTasksAdmin() {
  const { t, i18n } = useTranslation();
  const canReadTasks = useCan("school.tasks", "view");
  const canManageTasks = useCan("school.tasks", "manage");
  const canViewTasks = canReadTasks || canManageTasks;
  const canReadSignups = useCan("school.task-signups", "view");
  const canManageSignups = useCan("school.task-signups", "manage");
  const canViewSignups = canReadSignups || canManageSignups;
  const canReadCleaning = useCan("school.cleaning", "view");
  const canManageCleaning = useCan("school.cleaning", "manage");
  const canViewCleaning = canReadCleaning || canManageCleaning;
  const canListYears = useCan("school.years", "list");
  const canListClassrooms = useCan("school.classrooms", "list");
  const canListFamilies = useCan("families", "list");
  const [error, setError] = useState("");
  const [yearId, setYearId] = useState("");
  const dataPermissions = {
    canViewTasks,
    canViewSignups,
    canViewCleaning,
    canListYears,
    canListClassrooms,
    canListFamilies,
  };
  const data = useSchoolTasksAdminData({ permissions: dataPermissions, yearId });
  const { years, classrooms, families, familyChildren, suggestedYearId } = data.reference;
  const { tasks, activities, signups, moments, assignments, statuses } = data.year;
  // The backend decides which children of a family may be assigned for weekly cleaning.
  const cleaningChildIds = (familyId: string) => statuses.find((item) => item.familyId === familyId)?.cleaningChildIds || [];
  const { lookup: lookupErrors, section: sectionErrors, base: baseError } = data.errors;
  const { refreshYear } = data;
  const schoolTaskSettings = useSchoolTaskSettings(yearId, canViewSignups);
  const closedForParents = schoolTaskSettings.settings?.schoolYearId === yearId && !schoolTaskSettings.settings.parentAccessEnabled;
  const [activeTab, setActiveTab] = useState<
    | "activities"
    | "tasks"
    | "cleaning"
    | "obligations"
    | "settings"
  >("activities");
  const [taskSearch, setTaskSearch] = useState("");
  const [taskClassroom, setTaskClassroom] = useState("");
  const [taskType, setTaskType] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [taskRequirement, setTaskRequirement] = useState("");
  const [taskActivity, setTaskActivity] = useState("");
  const [taskView, setTaskView] = useState<"work" | "table">("work");
  const [taskFocus, setTaskFocus] = useState<"attention" | "open" | "full" | "all">("attention");
  const [activitySearch, setActivitySearch] = useState("");
  const [activityFilter, setActivityFilter] = useState<"all" | ActivityStatus>("all");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [form, setForm] = useState(emptyTask);
  const [editing, setEditing] = useState<string | null>(null);
  const [activityForm, setActivityForm] = useState(emptyActivity);
  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [taskPromptActivity, setTaskPromptActivity] = useState<SchoolActivity | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [momentForm, setMomentForm] = useState({
    id: "",
    startsAt: "",
    capacity: 1,
    description: "",
  });
  const [assignment, setAssignment] = useState({
    momentId: "",
    familyId: "",
    childId: "",
  });
  const [cleaningDrawerOpen, setCleaningDrawerOpen] = useState(false);
  const [selectedCleaningMomentId, setSelectedCleaningMomentId] = useState<string | null>(null);
  const [cleaningFilter, setCleaningFilter] = useState<"all" | "open">("all");
  const [collapsedCleaningMonths, setCollapsedCleaningMonths] = useState<Record<string, boolean>>({});
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [familySearch, setFamilySearch] = useState("");
  const [obligationFilter, setObligationFilter] = useState<"all" | "open" | "complete" | "exempt">("open");
  // Holds the school year the preview was opened for, so a year switch hides it at once.
  const [autoAssignmentPreviewYearId, setAutoAssignmentPreviewYearId] = useState<string | null>(null);
  useEffect(() => {
    if (!years.some((year) => year.id === yearId) && yearId !== suggestedYearId) {
      setYearId(suggestedYearId);
    }
  }, [suggestedYearId, yearId, years]);
  useEffect(() => {
    setEditing(null);
    setShowTaskForm(false);
    setForm({ ...emptyTask, schoolYearId: yearId });
    setTaskActivity("");
    setEditingActivity(null);
    setShowActivityForm(false);
    setActivityForm(emptyActivity);
    setCleaningDrawerOpen(false);
    setSelectedCleaningMomentId(null);
    setMomentForm({ id: "", startsAt: "", capacity: 1, description: "" });
    setAssignment({ momentId: "", familyId: "", childId: "" });
    setAutoAssignmentPreviewYearId(null);
  }, [yearId]);
  const yearClassrooms = classrooms.filter(
    (c) => String(c.schoolYear?.id || c.schoolYearId) === yearId,
  );
  const classroomMap = useMemo(
    () => new Map(classrooms.map((classroom) => [classroom.id, classroom.className])),
    [classrooms],
  );
  type TabId = typeof activeTab;
  const tabs = [
    canViewTasks && {
      id: "activities" as const,
      label: t("schoolTasks.activities", { defaultValue: "Activities" }),
    },
    canViewTasks && {
      id: "tasks" as const,
      label: t("schoolTasks.tasks", { defaultValue: "Tasks" }),
    },
    canViewCleaning && {
      id: "cleaning" as const,
      label: t("schoolTasks.cleaning", { defaultValue: "Cleaning" }),
    },
    canViewSignups && {
      id: "obligations" as const,
      label: t("schoolTasks.obligations", { defaultValue: "Obligations" }),
    },
    canViewSignups && {
      id: "settings" as const,
      label: t("schoolTasks.settings", { defaultValue: "Settings" }),
    },
  ].filter(Boolean) as Array<{
    id: TabId;
    label: string;
  }>;
  useEffect(() => {
    if (tabs.length && !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [activeTab, tabs]);
  const saveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description || null,
      type: form.type,
      schoolYearId: form.schoolYearId,
      classroomIds: form.classroomIds,
      activityId: form.activityId || null,
      startsAt: iso(form.startsAt),
      capacity: Number(form.capacity),
      optional: form.optional,
    };
    try {
      setError("");
      editing
        ? await teacherSchoolTasksApi.updateTask(editing, payload)
        : await teacherSchoolTasksApi.createTask(payload);
      setForm({ ...emptyTask, schoolYearId: yearId, activityId: selectedActivityId || "" });
      setEditing(null);
      setShowTaskForm(false);
      await refreshYear();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          t("schoolTasks.errors.save", {
            defaultValue: "Could not save the selection.",
          }),
      );
    }
  };
  const edit = (task: SchoolTask, stayOnActivity = false) => {
    if (!stayOnActivity) setActiveTab("tasks");
    setError("");
    setEditing(task.id);
    setShowTaskForm(true);
    setForm({
      ...task,
      classroomIds: task.classroomIds || [],
      startsAt: localInput(task.startsAt),
      activityId: task.activity?.id || "",
    });
  };
  const activityRows = useMemo(() => getSchoolActivityRows(activities, tasks), [activities, tasks]);
  const visibleActivities = useMemo(() => {
    return filterSchoolActivityRows(activityRows, activityFilter, activitySearch, i18n.language);
  }, [activityFilter, activityRows, activitySearch, i18n.language]);
  const defaultCreationYearId = getInitialSchoolYearId(years) || yearId;
  const saveActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { schoolYearId: activityForm.schoolYearId || defaultCreationYearId, name: activityForm.name, description: activityForm.description || null, startsAt: iso(activityForm.startsAt), endsAt: iso(activityForm.endsAt) };
    try {
      const createdActivity = editingActivity
        ? null
        : await teacherSchoolTasksApi.createActivity(payload);
      if (editingActivity) await teacherSchoolTasksApi.updateActivity(editingActivity, payload);
      setActivityForm(emptyActivity);
      setEditingActivity(null);
      setShowActivityForm(false);
      await refreshYear();
      if (createdActivity) setTaskPromptActivity(createdActivity);
    } catch (e: any) {
      setError(e?.response?.data?.message || t("schoolTasks.errors.saveActivity", { defaultValue: "Could not save the activity." }));
    }
  };
  const saveMoment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        schoolYearId: yearId,
        startsAt: iso(momentForm.startsAt),
        capacity: Number(momentForm.capacity),
        description: momentForm.description,
      };
      momentForm.id
        ? await teacherSchoolTasksApi.updateMoment(momentForm.id, payload)
        : await teacherSchoolTasksApi.createMoment(payload);
      setMomentForm({ id: "", startsAt: "", capacity: 1, description: "" });
      await refreshYear();
      return true;
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          t("schoolTasks.errors.save", {
            defaultValue: "Could not save the selection.",
          }),
      );
      return false;
    }
  };
  const assignCleaning = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await teacherSchoolTasksApi.assign(assignment);
      setAssignment({ momentId: "", familyId: "", childId: "" });
      await refreshYear();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          t("schoolTasks.errors.save", {
            defaultValue: "Could not save the selection.",
          }),
      );
    }
  };
  const activeSectionError =
    activeTab === "activities" || activeTab === "tasks"
      ? sectionErrors.tasks || lookupErrors.tasks
      : activeTab === "cleaning"
        ? sectionErrors.cleaning || lookupErrors.cleaning
        : activeTab === "obligations"
          ? sectionErrors.signups || lookupErrors.signups
          : undefined;
  const pageError = error || baseError || "";
  if (data.loading.base) return <Spinner />;
  const creatingTask = canManageTasks && showTaskForm && !editing;
  const selectedActivity = selectedActivityId ? activities.find((activity) => activity.id === selectedActivityId) : undefined;
  const obligationRows = getFamilyObligationRows(families, statuses, assignments);
  // The backend requires both manage permissions for the preview.
  const canPreviewAutoAssignment = canManageSignups && canManageCleaning && Boolean(yearId);
  const selectedYear = years.find((year) => year.id === yearId);
  const selectedObligationRow = selectedFamilyId ? obligationRows.find((row) => row.family.id === selectedFamilyId) : undefined;
  return (
    <PageTemplate title={null}>
      <div className="mx-auto max-w-7xl space-y-5 rounded-[32px] border border-slate-200/80 bg-white p-5 pb-10 shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-7 sm:pb-12">
        {canViewSignups && activeTab === "obligations" && selectedObligationRow && <ObligationsFamilyActions family={selectedObligationRow.family} status={selectedObligationRow.status} canManage={canManageSignups} onBack={() => setSelectedFamilyId(null)} onSetExempt={async (family, exempt) => { try { if (exempt) await teacherSchoolTasksApi.saveExemption({ familyId: family.id, schoolYearId: yearId, reason: "" }); else await teacherSchoolTasksApi.deleteExemption(family.id, yearId); await refreshYear(); } catch (e: any) { setError(e?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }} />}
        {!showActivityForm && !selectedActivityId && !creatingTask && <header className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-7 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-8">
          <FaCalendarAlt aria-hidden="true" className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 text-[7rem] text-sky-200/75 lg:block dark:text-sky-900/40" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between lg:pr-36">
            <div className="flex min-w-0 items-center gap-5">
              <div className="hidden h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-indigo-100 text-3xl text-indigo-600 shadow-md sm:flex dark:border-slate-900 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FaCalendarAlt aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  {t("schoolTasks.management", { defaultValue: "Management" })}
                </p>
                <h1 className="mt-1 text-3xl font-black text-slate-950 dark:text-white">
                  {t("schoolTasks.title", { defaultValue: "School tasks" })}
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  {t("schoolTasks.managementIntro", { defaultValue: "Manage tasks, signups, exemptions and cleaning schedules." })}
                </p>
                {closedForParents && <p role="status" className="mt-3 inline-flex rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-bold text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200">
                  {t("schoolTasks.notVisibleForParents", { defaultValue: "Not yet visible to parents" })}
                </p>}
              </div>
            </div>
            {canListYears && !showActivityForm && (
              <div className="shrink-0">
                <SchoolYearDropdown
                  schoolYears={years}
                  selectedSchoolYearId={yearId}
                  onChange={(nextYearId) => {
                    setSelectedActivityId(null);
                    setYearId(nextYearId);
                  }}
                  label={t("schoolTasks.schoolYear", { defaultValue: "School year" })}
                />
              </div>
            )}
          </div>
        </header>}
        {tabs.length > 0 && !showActivityForm && !selectedActivityId && !creatingTask && !selectedFamilyId && (
          <nav
            aria-label={t("schoolTasks.sections", {
              defaultValue: "School task sections",
            })}
            className="flex gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm dark:border-slate-800 dark:bg-slate-950"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                aria-current={activeTab === tab.id ? "page" : undefined}
                onClick={() => setActiveTab(tab.id)}
                className={`min-w-max rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}
        {pageError && !editing && (
          <div
            role="alert"
            className="rounded-2xl bg-red-50 p-4 font-bold text-red-700"
          >
            {pageError}
          </div>
        )}
        {activeSectionError && (
          <div
            role="alert"
            className="rounded-2xl bg-red-50 p-4 font-bold text-red-700"
          >
            {activeSectionError}
          </div>
        )}
        {!canListYears && (canViewTasks || canViewSignups || canViewCleaning) && (
          <div
            role="status"
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
          >
            {t("schoolTasks.yearAccessRequired", {
              defaultValue:
                "School year access is required to view year-specific school tasks.",
            })}
          </div>
        )}
        {canListYears && !pageError && years.length === 0 && (canViewTasks || canViewSignups || canViewCleaning) && (
          <div
            role="status"
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
          >
            {t("schoolTasks.noValidSchoolYears", {
              defaultValue: "No valid school years are available for school tasks.",
            })}
          </div>
        )}
        {canViewTasks && activeTab === "activities" && !showActivityForm && !creatingTask && (
          <ActivitiesSection
            rows={activityRows}
            visibleRows={visibleActivities}
            years={years}
            classroomMap={classroomMap}
            search={activitySearch}
            filter={activityFilter}
            selectedActivityId={selectedActivityId}
            canManage={canManageTasks}
            canCreate={canManageTasks && canListYears}
            locale={i18n.language}
            onSearchChange={setActivitySearch}
            onFilterChange={setActivityFilter}
            onCreate={() => { setEditingActivity(null); setActivityForm(newActivityDefaults(defaultCreationYearId)); setShowActivityForm(true); }}
            onSelect={setSelectedActivityId}
            onBack={() => { setSelectedActivityId(null); setShowTaskForm(false); setEditing(null); }}
            onEdit={(activity) => { setEditingActivity(activity.id); setActivityForm({ name: activity.name, description: activity.description || "", startsAt: localInput(activity.startsAt), endsAt: localInput(activity.endsAt), schoolYearId: activity.schoolYearId }); setShowActivityForm(true); }}
            onDelete={async (activity) => { if (!window.confirm(t("schoolTasks.deleteActivityConfirm", { defaultValue: "Delete activity {{name}}?", name: activity.name }))) return; try { await teacherSchoolTasksApi.deleteActivity(activity.id); setSelectedActivityId(null); await refreshYear(); } catch (error: any) { setError(error?.response?.data?.message || t("schoolTasks.errors.deleteActivity", { defaultValue: "This activity could not be deleted. Remove its task links first." })); } }}
            onAddTask={(activity) => { setEditing(null); setForm({ ...emptyTask, schoolYearId: activity.schoolYearId, activityId: activity.id, startsAt: localInput(activity.startsAt) }); setShowTaskForm(true); }}
            onEditTask={(task) => edit(task, true)}
            onDeleteTask={async (task) => { if (!window.confirm(t("schoolTasks.deleteTaskConfirm", { defaultValue: "Delete task {{name}}?", name: task.name }))) return; try { await teacherSchoolTasksApi.deleteTask(task.id); await refreshYear(); } catch (error: any) { setError(error?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }}
          />
        )}
        {creatingTask && (
          <div className="space-y-5">
            <div className="flex min-h-[72px] items-center rounded-[28px] border border-slate-200 bg-white px-6 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:px-8">
              <button type="button" onClick={() => { setShowTaskForm(false); setEditing(null); setForm({ ...emptyTask, schoolYearId: yearId, activityId: selectedActivityId || "" }); }} className="inline-flex min-h-11 items-center gap-3 text-base font-bold text-slate-600 transition hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-300 dark:hover:text-indigo-300">
                <FaArrowLeft aria-hidden="true" className="shrink-0 text-indigo-600 dark:text-indigo-400" />
                <span>{selectedActivity ? t("schoolTasks.backToActivity", { defaultValue: "Back to activity" }) : t("schoolTasks.backToTasks", { defaultValue: "Back to tasks" })}</span>
              </button>
            </div>

            <section className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-r from-white via-white to-sky-50 shadow-sm dark:border-white/10 dark:from-slate-900 dark:via-slate-900 dark:to-sky-950/30">
              <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[38%] rounded-l-[120px] bg-sky-100/70 lg:block dark:bg-sky-950/30" />
              <FaTasks aria-hidden="true" className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-sky-200/90 lg:block dark:text-sky-900/50" />
              <div className="relative flex min-h-[180px] items-center gap-6 px-7 py-7 sm:px-9 lg:pr-[40%]">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-indigo-100 text-4xl text-indigo-600 shadow-md dark:border-slate-900 dark:bg-indigo-950/70 dark:text-indigo-300"><FaTasks /></div>
                <div className="min-w-0">
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">{t("schoolTasks.taskDetails", { defaultValue: "Task details" })}</p>
                  <h1 className="mt-1 text-3xl font-black text-slate-950 dark:text-white sm:text-4xl">{t("schoolTasks.newTask", { defaultValue: "New task" })}</h1>
                  <p className="mt-2 text-sm font-semibold text-slate-600 dark:text-slate-300">{selectedActivity ? t("schoolTasks.addTaskToActivity", { defaultValue: "Add a task to {{activity}}.", activity: selectedActivity.name }) : t("schoolTasks.addTaskForYear", { defaultValue: "Create a task for school year {{year}}.", year: (() => { const taskYear = years.find((year) => year.id === form.schoolYearId); return taskYear ? getSchoolYearLabel(taskYear) : ""; })() })}</p>
                </div>
              </div>
            </section>

            <form id="task-create-form" onSubmit={saveTask} className="space-y-5">
              <div className="grid gap-5 lg:grid-cols-2">
                <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">{t("schoolTasks.generalDetails", { defaultValue: "General details" })}</h2>
                  <div className="mt-4 space-y-4">
                    <Input label={t("schoolTasks.name", { defaultValue: "Name" })} required value={form.name} onChange={(name) => setForm((previous) => ({ ...previous, name }))} />
                    <label className="grid gap-1 text-sm font-bold text-slate-900 dark:text-white">{t("schoolTasks.description", { defaultValue: "Description" })}<textarea value={form.description} onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))} className="min-h-24 rounded-xl border border-slate-200 bg-white p-3 font-normal outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950" /></label>
                  </div>
                </section>

                <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">{t("schoolTasks.planning", { defaultValue: "Planning" })}</h2>
                  <div className="mt-4 space-y-4">
                    <SchoolYearDropdown schoolYears={years} selectedSchoolYearId={form.schoolYearId} onChange={(schoolYearId) => setForm((previous) => ({ ...previous, schoolYearId, activityId: activities.some((activity) => activity.id === previous.activityId && activity.schoolYearId === schoolYearId) ? previous.activityId : "", classroomIds: [] }))} label={t("schoolTasks.schoolYear", { defaultValue: "School year" })} />
                    <label className="grid gap-1 text-sm font-bold">{t("schoolTasks.activity", { defaultValue: "Activity" })}<select aria-label={t("schoolTasks.activity", { defaultValue: "Activity" })} value={form.activityId} onChange={(event) => { const activityId = event.target.value; const activity = activities.find((candidate) => candidate.id === activityId); setForm((previous) => ({ ...previous, activityId, startsAt: activity?.startsAt ? localInput(activity.startsAt) : previous.startsAt })); }} className="rounded-xl border border-slate-200 bg-white p-3 font-normal dark:border-slate-700 dark:bg-slate-950"><option value="">{t("schoolTasks.withoutActivity", { defaultValue: "Without activity" })}</option>{activities.filter((activity) => activity.schoolYearId === form.schoolYearId).map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}</select></label>
                    <Input label={t("schoolTasks.dateTime", { defaultValue: "Date and time" })} type="datetime-local" value={form.startsAt} onChange={(startsAt) => setForm((previous) => ({ ...previous, startsAt }))} />
                    <Input label={t("schoolTasks.capacity", { defaultValue: "Capacity" })} type="number" min={1} required value={String(form.capacity)} onChange={(capacity) => setForm((previous) => ({ ...previous, capacity: Number(capacity) }))} />
                  </div>
                </section>
              </div>

              <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
                <h2 className="text-lg font-black text-slate-950 dark:text-white">{t("schoolTasks.assignment", { defaultValue: "Assignment" })}</h2>
                <div className="mt-4 grid gap-5 lg:grid-cols-2">
                  <label className="grid gap-1 text-sm font-bold">{t("schoolTasks.type", { defaultValue: "Type" })}<select aria-label={t("schoolTasks.type", { defaultValue: "Type" })} value={form.type} onChange={(event) => setForm((previous) => ({ ...previous, type: event.target.value as SchoolTaskType }))} className="rounded-xl border border-slate-200 bg-white p-3 font-normal dark:border-slate-700 dark:bg-slate-950"><option value="PARENT_TASK">{t("schoolTasks.types.parent", { defaultValue: "Parent task" })}</option><option value="GENERAL_TASK">{t("schoolTasks.types.general", { defaultValue: "General task" })}</option><option value="HOLIDAY_CLEANING">{t("schoolTasks.types.holiday", { defaultValue: "Holiday cleaning" })}</option></select></label>
                  <label className="flex min-h-[48px] items-center gap-3 self-end rounded-xl border border-slate-200 px-4 text-sm font-bold dark:border-slate-700"><input type="checkbox" checked={form.optional} onChange={(event) => setForm((previous) => ({ ...previous, optional: event.target.checked }))} />{t("schoolTasks.optional", { defaultValue: "Optional" })}</label>
                  {form.type === "PARENT_TASK" && <fieldset className="lg:col-span-2"><legend className="text-sm font-bold">{t("schoolTasks.classrooms", { defaultValue: "Classrooms" })}</legend><div className="mt-3 flex flex-wrap gap-2">{classrooms.filter((classroom) => String(classroom.schoolYear?.id || classroom.schoolYearId) === form.schoolYearId).map((classroom) => { const selected = form.classroomIds.includes(classroom.id); return <label key={classroom.id} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-sm font-bold transition ${selected ? "border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"}`}><input className="sr-only" type="checkbox" checked={selected} onChange={() => setForm((previous) => ({ ...previous, classroomIds: selected ? previous.classroomIds.filter((id) => id !== classroom.id) : [...previous.classroomIds, classroom.id] }))} />{classroom.className}</label>; })}</div></fieldset>}
                </div>
              </section>

              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={() => { setShowTaskForm(false); setEditing(null); setForm({ ...emptyTask, schoolYearId: yearId, activityId: selectedActivityId || "" }); }} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">{t("common.cancel", { defaultValue: "Cancel" })}</button>
                <button type="submit" className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700">{t("schoolTasks.addTask", { defaultValue: "Add task" })}</button>
              </div>
            </form>
          </div>
        )}
        {canManageTasks && showActivityForm && activeTab === "activities" && (
          <div className="rounded-[34px] border border-slate-200/80 bg-white p-5 shadow-[0_18px_55px_rgba(15,23,42,0.08)] dark:border-slate-700/80 dark:bg-slate-950 sm:p-6 lg:p-7">
            <div className="space-y-5">
            <div className="flex min-h-[72px] items-center rounded-[28px] border border-slate-200 bg-white px-6 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:px-8">
              <button
                type="button"
                onClick={() => { setShowActivityForm(false); setEditingActivity(null); setActivityForm(emptyActivity); }}
                className="inline-flex min-h-11 items-center gap-3 text-base font-bold text-slate-600 transition hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-300 dark:hover:text-indigo-300"
              >
                <FaArrowLeft aria-hidden="true" className="shrink-0 text-indigo-600 dark:text-indigo-400" />
                <span>{t("schoolTasks.backToActivities", { defaultValue: "Back to activities" })}</span>
              </button>
            </div>

            <section className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-r from-white via-white to-sky-50 shadow-sm dark:border-white/10 dark:from-slate-900 dark:via-slate-900 dark:to-sky-950/30">
              <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[38%] rounded-l-[120px] bg-sky-100/70 lg:block dark:bg-sky-950/30" />
              <FaCalendarAlt aria-hidden="true" className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-sky-200/90 lg:block dark:text-sky-900/50" />
              <div className="relative flex min-h-[180px] items-center gap-6 px-7 py-7 sm:px-9 lg:pr-[40%]">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-indigo-100 text-4xl text-indigo-600 shadow-md dark:border-slate-900 dark:bg-indigo-950/70 dark:text-indigo-300">
                  {getActivityVisual(activityForm.name).icon}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                    {t("schoolTasks.activityDetails", { defaultValue: "Activity details" })}
                  </p>
                  <h1 className="mt-1 text-3xl font-black text-slate-950 dark:text-white sm:text-4xl">
                    {editingActivity
                      ? (activityForm.name || t("schoolTasks.editActivity", { defaultValue: "Edit activity" }))
                      : t("schoolTasks.newActivity", { defaultValue: "New activity" })}
                  </h1>
                  <p className="mt-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {editingActivity
                      ? t("schoolTasks.editActivityIntro", { defaultValue: "Update the activity details." })
                      : t("schoolTasks.addActivityForYear", { defaultValue: "Create an activity for school year {{year}}.", year: (() => { const activityYear = years.find((year) => year.id === activityForm.schoolYearId); return activityYear ? getSchoolYearLabel(activityYear) : ""; })() })}
                  </p>
                </div>
              </div>
            </section>

            <form onSubmit={saveActivity} className="space-y-5">
              <div className="grid gap-5 lg:grid-cols-2">
                <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">{t("schoolTasks.generalDetails", { defaultValue: "General details" })}</h2>
                  <div className="mt-4 space-y-4">
                    <SchoolYearDropdown schoolYears={years} selectedSchoolYearId={activityForm.schoolYearId} onChange={(schoolYearId) => setActivityForm((previous) => ({ ...previous, schoolYearId }))} label={t("schoolTasks.schoolYear", { defaultValue: "School year" })} />
                    <Input label={t("schoolTasks.name", { defaultValue: "Name" })} required value={activityForm.name} onChange={(name) => setActivityForm((p) => ({ ...p, name }))} />
                    <label className="grid gap-1 text-sm font-bold text-slate-900 dark:text-white">
                      {t("schoolTasks.description", { defaultValue: "Description" })}
                      <textarea value={activityForm.description} onChange={(e) => setActivityForm((p) => ({ ...p, description: e.target.value }))} className="min-h-24 rounded-xl border border-slate-200 bg-white p-3 font-normal outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-950" />
                    </label>
                  </div>
                </section>

                <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
                  <h2 className="text-lg font-black text-slate-950 dark:text-white">{t("schoolTasks.planning", { defaultValue: "Planning" })}</h2>
                  <div className="mt-4 space-y-4">
                    <Input label={t("schoolTasks.startsAt", { defaultValue: "Starts at" })} type="datetime-local" value={activityForm.startsAt} onChange={(startsAt) => setActivityForm((p) => ({ ...p, startsAt }))} />
                    <Input label={t("schoolTasks.endsAt", { defaultValue: "Ends at" })} type="datetime-local" value={activityForm.endsAt} onChange={(endsAt) => setActivityForm((p) => ({ ...p, endsAt }))} />
                  </div>
                </section>
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={() => { setShowActivityForm(false); setEditingActivity(null); setActivityForm(emptyActivity); }} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">{t("common.cancel", { defaultValue: "Cancel" })}</button>
                <button type="submit" className="rounded-full bg-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700">{editingActivity ? t("common.save", { defaultValue: "Save changes" }) : t("schoolTasks.addActivity", { defaultValue: "Add activity" })}</button>
              </div>
            </form>
            </div>
          </div>
        )}
        {canViewTasks && activeTab === "tasks" && !creatingTask && (
          <Panel
            icon={<FaTasks />}
            title={t("schoolTasks.tasks", { defaultValue: "Tasks" })}
            description={t("schoolTasks.tasksIntro", {
              defaultValue: "See where help is still needed and manage all tasks for the selected school year.",
            })}
          >
            <TasksSection
              tasks={tasks}
              activities={activities}
              classrooms={yearClassrooms}
              classroomMap={classroomMap}
              filters={{ search: taskSearch, classroomId: taskClassroom, type: taskType as SchoolTaskType | "", status: taskStatus as "available" | "full" | "", requirement: taskRequirement as "required" | "optional" | "", activityId: taskActivity }}
              onFiltersChange={(next) => { if (next.search !== undefined) setTaskSearch(next.search); if (next.classroomId !== undefined) setTaskClassroom(next.classroomId); if (next.type !== undefined) setTaskType(next.type); if (next.status !== undefined) setTaskStatus(next.status); if (next.requirement !== undefined) setTaskRequirement(next.requirement); if (next.activityId !== undefined) setTaskActivity(next.activityId); }}
              view={taskView}
              onViewChange={setTaskView}
              focus={taskFocus}
              onFocusChange={setTaskFocus}
              canManage={canManageTasks}
              canCreate={canManageTasks && canListYears}
              locale={i18n.language}
              onCreate={() => { setEditing(null); setForm({ ...emptyTask, schoolYearId: defaultCreationYearId }); setShowTaskForm(true); }}
              onEdit={edit}
              onDelete={async (task) => { if (!window.confirm(t("schoolTasks.deleteTaskConfirm", { defaultValue: "Delete task {{name}}?", name: task.name }))) return; try { await teacherSchoolTasksApi.deleteTask(task.id); await refreshYear(); } catch (e: any) { setError(e?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }}
            />
          </Panel>
        )}
        {canViewSignups && activeTab === "obligations" && (
          <ObligationsSection
            rows={obligationRows}
            selectedFamilyId={selectedFamilyId}
            filter={obligationFilter}
            assignments={assignments}
            moments={moments}
            signups={signups}
            tasks={tasks}
            classrooms={classrooms}
            familyChildren={familyChildren}
            locale={i18n.language}
            onFilterChange={setObligationFilter}
            onSelectFamily={setSelectedFamilyId}
            onOpenAutoAssignmentPreview={canPreviewAutoAssignment ? () => setAutoAssignmentPreviewYearId(yearId) : undefined}
          />
        )}

        {canViewSignups && activeTab === "settings" && <SettingsSection settings={schoolTaskSettings.settings} loadFailed={schoolTaskSettings.loadFailed} canManage={canManageSignups} onSaved={schoolTaskSettings.setSettings} />}

        {canViewCleaning && activeTab === "cleaning" && <CleaningSection
          moments={moments}
          assignments={assignments}
          families={families}
          filter={cleaningFilter}
          collapsedMonths={collapsedCleaningMonths}
          canManage={canManageCleaning}
          canCreate={canListYears}
          locale={i18n.language}
          onFilterChange={setCleaningFilter}
          onToggleMonth={(monthKey, collapsed) => setCollapsedCleaningMonths((previous) => ({ ...previous, [monthKey]: collapsed }))}
          onCreate={() => { setMomentForm({ id: "", startsAt: "", capacity: 1, description: "" }); setAssignment({ momentId: "", familyId: "", childId: "" }); setSelectedCleaningMomentId(null); setCleaningDrawerOpen(true); }}
          onSelectMoment={(moment) => { setSelectedCleaningMomentId(moment.id); setMomentForm({ id: moment.id, startsAt: localInput(moment.startsAt), capacity: moment.capacity, description: moment.description || "" }); setAssignment({ momentId: moment.id, familyId: "", childId: "" }); setCleaningDrawerOpen(true); }}
        />}

        {canManageCleaning && cleaningDrawerOpen && (
          <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setCleaningDrawerOpen(false); }}>
            <aside role="dialog" aria-modal="true" aria-labelledby="cleaning-moment-title" className="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-700">
                <div><p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600">{t("schoolTasks.cleaning", { defaultValue: "Cleaning" })}</p><h2 id="cleaning-moment-title" className="mt-1 text-xl font-black">{selectedCleaningMomentId ? t("schoolTasks.editCleaningMoment", { defaultValue: "Edit cleaning moment" }) : t("schoolTasks.addMoment", { defaultValue: "Add cleaning moment" })}</h2></div>
                <button type="button" aria-label={t("common.close", { defaultValue: "Close" })} onClick={() => setCleaningDrawerOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full text-2xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                <form id="cleaning-moment-form" onSubmit={async (event) => { if (await saveMoment(event)) { setSelectedCleaningMomentId(null); setCleaningDrawerOpen(false); } }} className="grid gap-5">
                  <div><h3 className="font-black text-slate-950 dark:text-white">{t("schoolTasks.planning", { defaultValue: "Planning" })}</h3><p className="mt-1 text-sm text-slate-500">{t("schoolTasks.cleaningMomentPlanningHelp", { defaultValue: "Set the date, capacity and information for this cleaning moment." })}</p></div>
                  <Input label={t("schoolTasks.dateTime", { defaultValue: "Date and time" })} type="datetime-local" required value={momentForm.startsAt} onChange={(v) => setMomentForm((p) => ({ ...p, startsAt: v }))} />
                  <Input label={t("schoolTasks.capacity", { defaultValue: "Capacity" })} type="number" required value={String(momentForm.capacity)} onChange={(v) => setMomentForm((p) => ({ ...p, capacity: Number(v) }))} />
                  <Input label={t("schoolTasks.information", { defaultValue: "Information" })} value={momentForm.description} onChange={(v) => setMomentForm((p) => ({ ...p, description: v }))} />
                </form>

                {selectedCleaningMomentId && (() => {
                  const currentAssignments = assignments.filter((item) => item.momentId === selectedCleaningMomentId);
                  return <div className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-700">
                    <div className="flex items-center justify-between gap-3"><div><h3 className="font-black text-slate-950 dark:text-white">{t("schoolTasks.assignments", { defaultValue: "Assignments" })}</h3><p className="mt-1 text-sm text-slate-500">{currentAssignments.length} / {momentForm.capacity} {t("schoolTasks.placesFilled", { defaultValue: "places filled" })}</p></div></div>
                    <div className="mt-4 grid gap-2">
                      {currentAssignments.map((item) => {
                        const family = families.find((f) => f.id === item.familyId);
                        const child = (familyChildren[item.familyId] || []).find((c) => c.id === item.childId);
                        return <div key={item.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 dark:border-slate-700"><div className="min-w-0 flex-1"><p className="font-bold">{family?.familyName || item.familyId}</p><p className="text-sm text-slate-500">{child ? [child.firstName, child.lastName].filter(Boolean).join(" ") : t("schoolTasks.noChildSelected", { defaultValue: "No child selected" })}</p></div><button type="button" aria-label={t("common.delete", { defaultValue: "Delete" })} onClick={async () => { try { await teacherSchoolTasksApi.deleteAssignment(item.id); await refreshYear(); } catch (e: any) { setError(e?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"><FaTrash /></button></div>;
                      })}
                      {currentAssignments.length === 0 && <p className="rounded-xl bg-slate-50 px-4 py-4 text-sm text-slate-500 dark:bg-slate-800/60">{t("schoolTasks.noAssignments", { defaultValue: "No families have been assigned yet." })}</p>}
                    </div>
                    {currentAssignments.length < Number(momentForm.capacity) && <div className="mt-5 rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/60"><p className="mb-3 text-sm font-black text-slate-800 dark:text-slate-100">{t("schoolTasks.assignFamily", { defaultValue: "Assign family" })}</p><div className="grid gap-3"><Select label={t("schoolTasks.family", { defaultValue: "Family" })} value={assignment.familyId} onChange={(v) => setAssignment((p) => ({ ...p, familyId: v, childId: "" }))} options={families.map((f) => ({ value: f.id, label: f.familyName || f.id }))} /><Select label={t("schoolTasks.child", { defaultValue: "Child" })} value={assignment.childId} onChange={(v) => setAssignment((p) => ({ ...p, childId: v }))} options={(familyChildren[assignment.familyId] || []).filter((c) => cleaningChildIds(assignment.familyId).includes(String(c.id))).map((c) => ({ value: c.id, label: [c.firstName, c.lastName].filter(Boolean).join(" ") }))} />{assignment.familyId && (() => { const familyStatus = statuses.find((item) => item.familyId === assignment.familyId); return <><p className="text-sm text-slate-500">{t("schoolTasks.cleaningScheduledCount", { defaultValue: "{{count}} turns scheduled", count: familyStatus?.cleaningAssignedCount || 0 })}</p>{familyStatus?.cleaningChildrenUndetermined && <p className="text-sm font-bold text-amber-800 dark:text-amber-200">{cleaningChildrenUndeterminedText(t, familyStatus.cleaningChildrenUndetermined)}</p>}</>; })()}<button type="button" disabled={!assignment.familyId || !assignment.childId} onClick={async () => { try { await teacherSchoolTasksApi.assign(assignment); setAssignment({ momentId: selectedCleaningMomentId, familyId: "", childId: "" }); await refreshYear(); } catch (e: any) { setError(e?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }} className="justify-self-start rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-bold text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-800 dark:bg-slate-900 dark:text-indigo-300">{t("schoolTasks.assign", { defaultValue: "Assign" })}</button></div></div>}
                  </div>;
                })()}
              </div>
              <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-900"><div className="flex items-center justify-between gap-3">{selectedCleaningMomentId ? <button type="button" onClick={async () => { if (!selectedCleaningMomentId) return; try { await teacherSchoolTasksApi.deleteMoment(selectedCleaningMomentId); setCleaningDrawerOpen(false); setSelectedCleaningMomentId(null); await refreshYear(); } catch (e: any) { setError(e?.response?.data?.message || t("schoolTasks.errors.save", { defaultValue: "Could not save the selection." })); } }} className="rounded-xl px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950">{t("common.delete", { defaultValue: "Delete" })}</button> : <span />}<div className="flex gap-3"><button type="button" onClick={() => setCleaningDrawerOpen(false)} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold dark:border-slate-700">{t("common.cancel", { defaultValue: "Cancel" })}</button><button type="submit" form="cleaning-moment-form" disabled={!momentForm.startsAt || momentForm.capacity < 1} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50">{selectedCleaningMomentId ? t("common.save", { defaultValue: "Save changes" }) : t("schoolTasks.addMoment", { defaultValue: "Add cleaning moment" })}</button></div></div></div>
            </aside>
          </div>
        )}

        {canManageTasks && editing && showTaskForm && typeof document !== "undefined" && createPortal(
          <div className="fixed inset-0 z-[9999] flex justify-end bg-slate-950/35 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) { setEditing(null); setShowTaskForm(false); } }}>
            <aside role="dialog" aria-modal="true" aria-labelledby="task-edit-title" className="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-900">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">{t("schoolTasks.task", { defaultValue: "Task" })}</p>
                  <h2 id="task-edit-title" className="mt-1 text-xl font-black text-slate-950 dark:text-white">{t("schoolTasks.editTask", { defaultValue: "Edit task" })}</h2>
                </div>
                <button type="button" aria-label={t("common.close", { defaultValue: "Close" })} onClick={() => { setEditing(null); setShowTaskForm(false); }} className="flex h-10 w-10 items-center justify-center rounded-full text-2xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white">×</button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                {error && <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">{error}</div>}
                <SchoolTaskEditor form={form} setForm={setForm} isEditing={Boolean(editing)} activities={activities} classrooms={classrooms} years={years} onSubmit={saveTask} onCancel={() => { setEditing(null); setShowTaskForm(false); setForm({ ...emptyTask, schoolYearId: yearId, activityId: selectedActivityId || "" }); }} formId="task-edit-form" hideActions />
              </div>
              <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-end gap-3">
                  <button type="button" onClick={() => { setEditing(null); setShowTaskForm(false); setForm({ ...emptyTask, schoolYearId: yearId, activityId: selectedActivityId || "" }); }} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">{t("common.cancel", { defaultValue: "Cancel" })}</button>
                  <button type="submit" form="task-edit-form" className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700">{t("common.saveChanges", { defaultValue: "Save changes" })}</button>
                </div>
              </div>
            </aside>
          </div>,
          document.body,
        )}
        {canPreviewAutoAssignment && <AutoAssignmentPreviewDialog
          show={autoAssignmentPreviewYearId === yearId}
          schoolYearId={yearId}
          schoolYearLabel={selectedYear ? getSchoolYearLabel(selectedYear) : ""}
          families={families}
          familyChildren={familyChildren}
          tasks={tasks}
          locale={i18n.language}
          onClose={() => setAutoAssignmentPreviewYearId(null)}
          onAssigned={() => void refreshYear()}
        />}
        <Modal
          show={Boolean(taskPromptActivity)}
          type="manage"
          variant="lds"
          dialogClassName="sm:max-w-md"
          title={t("schoolTasks.addTaskAfterActivityTitle", { defaultValue: "Add a task now?" })}
          message={t("schoolTasks.addTaskAfterActivityMessage", { defaultValue: "{{activity}} was created. Would you like to add its first task now?", activity: taskPromptActivity?.name || "" })}
          onClose={() => setTaskPromptActivity(null)}
          footer={<div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end"><button type="button" onClick={() => setTaskPromptActivity(null)} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">{t("schoolTasks.notNow", { defaultValue: "Not now" })}</button><button type="button" autoFocus onClick={() => { if (!taskPromptActivity) return; setSelectedActivityId(taskPromptActivity.id); setEditing(null); setForm({ ...emptyTask, schoolYearId: taskPromptActivity.schoolYearId, activityId: taskPromptActivity.id, startsAt: localInput(taskPromptActivity.startsAt) }); setShowTaskForm(true); setTaskPromptActivity(null); }} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700">{t("schoolTasks.addTaskNow", { defaultValue: "Yes, add a task" })}</button></div>}
        />
      </div>
    </PageTemplate>
  );
}
function Panel({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <h2 className={`${description ? "mb-1" : "mb-4"} flex items-center gap-3 text-xl font-black text-slate-950 dark:text-white`}>
        <span className="text-indigo-600">{icon}</span>
        {title}
      </h2>
      {description && <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">{description}</p>}
      {children}
    </section>
  );
}
function Input({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  min,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  min?: number;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      {label}
      <input
        required={required}
        min={min}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border p-3 dark:bg-slate-900"
      />
    </label>
  );
}
function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      {label}
      <select
        aria-label={label}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border p-3 dark:bg-slate-900"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
